import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button asChild variant="outline" className="mb-6">
          <a href="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund and Returns Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Your Right to Cancel (14-Day Cooling Off Period)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the Consumer Contracts Regulations 2013, you have the right to cancel your order within 14 days of receiving your product, without giving any reason.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise your right to cancel, you must inform us of your decision by:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email: contact@comparebeforeyoubuy.shop</li>
              <li>Phone: 08006521866 (UK Customer Support)</li>
              <li>Post: 91 CC/A Phase 4 DHA Lahore, Lahore, 54000, Pakistan</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Return Conditions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To be eligible for a return, your item must be:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>In its original condition and packaging</li>
              <li>Unused and undamaged</li>
              <li>Complete with all original accessories, manuals, and documentation</li>
              <li>With all seals and shrink wrap intact (where applicable)</li>
              <li>Returned within 14 days of notifying us of cancellation</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4 font-semibold text-red-600">
              Important: Products that have been opened, used, or damaged cannot be returned under the 14-day cooling off period, unless faulty.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Return Process</h2>
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
              <p className="font-semibold text-gray-900 mb-2">Step-by-Step:</p>
              <ol className="list-decimal pl-6 text-gray-700">
                <li className="mb-2">Contact us within 14 days of receiving your order</li>
                <li className="mb-2">We'll provide return instructions and address</li>
                <li className="mb-2">Pack the item securely in original packaging</li>
                <li className="mb-2">Return via tracked delivery service</li>
                <li className="mb-2">We'll inspect the item upon receipt</li>
                <li className="mb-2">Refund processed within 14 days if approved</li>
              </ol>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Return Shipping Costs</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Change of Mind:</strong> You are responsible for return shipping costs</li>
              <li><strong>Faulty Product:</strong> We cover return shipping costs</li>
              <li><strong>Wrong Item Sent:</strong> We cover return shipping and send correct item</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We recommend using tracked delivery service. We are not responsible for items lost in transit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Refund Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Once we receive and inspect your return:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>We'll notify you of approval or rejection</li>
              <li>Approved refunds are processed within 14 days</li>
              <li>Refunds are issued to the original payment method</li>
              <li>It may take 5-10 business days for your bank to process</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Deductions:</strong> If the item shows signs of use beyond what's necessary to establish its nature and functionality, we may make deductions from your refund.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Faulty or Damaged Products</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If your product arrives faulty or damaged:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Contact us immediately with photos of the damage</li>
              <li>We'll arrange collection or provide a returns label</li>
              <li>You'll receive a full refund or replacement</li>
              <li>No deduction for inspection of faulty goods</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              <strong>Time Limits:</strong> Report damage within 48 hours of delivery for transit damage. For manufacturing faults, contact us as soon as the fault appears.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Non-Returnable Items</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The following cannot be returned unless faulty:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>SIM cards that have been activated</li>
              <li>Products with broken seals (hygiene/safety)</li>
              <li>Digital products or software once downloaded</li>
              <li>Custom-ordered items</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Exchanges</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not offer direct exchanges. If you want a different product:
            </p>
            <ol className="list-decimal pl-6 mb-4 text-gray-700">
              <li>Return your original item for a refund</li>
              <li>Place a new order for the item you want</li>
            </ol>
            <p className="text-gray-700 leading-relaxed mb-4">
              This ensures you get the product you want as quickly as possible.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Warranty Claims</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For manufacturer warranty claims:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Contact us first - we'll guide you through the process</li>
              <li>Warranty period varies by manufacturer (typically 12 months)</li>
              <li>Warranty covers manufacturing defects, not accidental damage</li>
              <li>You may need to contact the manufacturer directly for some claims</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Late or Missing Refunds</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you haven't received your refund:
            </p>
            <ol className="list-decimal pl-6 mb-4 text-gray-700">
              <li>Check your bank account again</li>
              <li>Contact your bank (processing can take time)</li>
              <li>Contact your credit card company</li>
              <li>If still no refund, contact us at contact@comparebeforeyoubuy.shop</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Consumer Rights Act 2015</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Your statutory rights are not affected by this policy. Under the Consumer Rights Act 2015, you have rights if goods are faulty or not as described, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Up to 30 days: Full refund if faulty</li>
              <li>Up to 6 months: Repair or replacement</li>
              <li>Up to 6 years: Rights for significant faults</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For returns or refund questions:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">Contact Information</p>
              <p className="text-gray-700">Compare Before You Buy</p>
              <p className="text-gray-700">91 CC/A Phase 4 DHA Lahore</p>
              <p className="text-gray-700">Lahore, 54000</p>
              <p className="text-gray-700">Pakistan</p>
              <p className="text-gray-700 mt-4">
                Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a>
              </p>
              <p className="text-gray-700">
                Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a>
              </p>
              <p className="text-gray-700 mt-2">
                Hours: Monday-Friday 9am-6pm, Saturday 10am-4pm
              </p>
            </div>
          </section>

          <div className="bg-green-50 border-l-4 border-green-600 p-6 mt-8">
            <p className="font-semibold text-gray-900 mb-2">💡 Quick Tips for Smooth Returns:</p>
            <ul className="list-disc pl-6 text-gray-700">
              <li>Keep original packaging until you're sure you want to keep the product</li>
              <li>Test the product carefully within the 14-day period</li>
              <li>Use tracked shipping when returning items</li>
              <li>Contact us first - we're here to help!</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
